import React, { useEffect, useState, useCallback } from "react";
import { Button, Modal, Row, Col, Spinner, Badge, Card, InputGroup, Form, ListGroup } from "react-bootstrap";
import { LanguageProvider } from "../../../core/language-provider";
import { ViewManager } from "../../../core/view-manager";
import { DataManager } from "../../../core/data-manager";
import { toast } from "react-toastify";
import { settings } from "../../../config/settings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * ModalEntityConfigDnDNative (multi-preference)
 * - Sinistra: colonne selezionate (ordinabili via drag&drop nativo HTML5)
 * - Destra (top): tutte le colonne disponibili (indicazione usate / disponibili)
 * - Destra (bottom): preferenze salvate (lista), con azioni: Modifica, Imposta default, Elimina, Rinomina
 * - In basso a sinistra: salva modifiche e/o salva come nuova preferenza (con etichetta)
 *
 * user_entity_preferences campi: content, entity, label, isDefault
 * - entityAllPreferences: array di oggetti preferenza { id, entity, label, content, isDefault }
 * - entityPreferences: contenuto della preferenza attiva (quella con isDefault = 1)
 */
export default function ModalEntityConfigDnDNative({ entity, props, entityPreferences, entityAllPreferences, status, close }) {
    const [saving, setSaving] = useState(false);
    const [allColumns, setAllColumns] = useState([]);
    const [selected, setSelected] = useState([]);
    const [newFields, setNewFields] = useState(null);

    // Preferenze salvate
    const [prefList, setPrefList] = useState([]); // [{id,label,content,isDefault,entity}]
    const [activePrefId, setActivePrefId] = useState(null); // id preferenza su cui si sta lavorando

    // Rename state
    const [renamingId, setRenamingId] = useState(null);
    const [renamingLabel, setRenamingLabel] = useState("");

    // Sorting
    const [sortingField, setSortingField] = useState('id');
    const [sortingOrder, setSortingOrder] = useState('desc');

    // Stato UI drag&drop
    const [dragging, setDragging] = useState(null); // { item, source: 'selected'|'available', index }
    const [overList, setOverList] = useState(null); // 'selected' | 'available' | null
    const [placeholderIndex, setPlaceholderIndex] = useState(null); // indice di inserimento in selected

    // Inizializzazione
    useEffect(() => {
        if (!entity) return;
        const cols = (ViewManager.getColumns(entity) || []).slice();
        setAllColumns(cols);

        // Lista preferenze da props
        const list = Array.isArray(entityAllPreferences) ? entityAllPreferences.slice() : [];
        setPrefList(list);

        // Scegli preferenza attiva: quella con isDefault=1; se non c'è, usa entityPreferences
        const def = list.find((p) => Number(p.isDefault) === 1);
        if (def && def.content) {
            let content;
            try {
                content = typeof def.content === "string" ? JSON.parse(def.content) : def.content;
            } catch (_) {
                content = def.content || {};
            }
            const prefSelected = (content?.columns || []).filter((c) => cols.includes(c));
            setSelected(prefSelected);
            setNewFields({ columns: prefSelected, sorting: content?.sorting || null });
            setActivePrefId(def.id ?? null);
            if (content?.sorting) {
                setSortingField(content.sorting.field || 'id');
                setSortingOrder(content.sorting.order || 'desc');
            } else {
                setSortingField('id');
                setSortingOrder('desc');
            }
        } else {
            // fallback a entityPreferences
            const prefSelected = (entityPreferences?.columns || []).filter((c) => cols.includes(c));
            setSelected(prefSelected);
            setNewFields({ columns: prefSelected, sorting: entityPreferences?.sorting || null });
            setActivePrefId(null);
            if (entityPreferences?.sorting) {
                setSortingField(entityPreferences.sorting.field || 'id');
                setSortingOrder(entityPreferences.sorting.order || 'desc');
            } else {
                setSortingField('id');
                setSortingOrder('desc');
            }
        }
    }, [entity, entityPreferences, entityAllPreferences]);

    // Helpers stato
    const syncSelected = useCallback((next) => {
        setSelected(next);
        setNewFields(prev => ({ ...prev, columns: next }));
    }, []);

    const addColumn = useCallback(
        (col, atIndex = null) => {
            if (!col) return;
            if (selected.includes(col)) return;
            const insertIndex = atIndex === null ? selected.length : Math.max(0, Math.min(atIndex, selected.length));
            const next = [...selected];
            next.splice(insertIndex, 0, col);
            syncSelected(next);
        },
        [selected, syncSelected]
    );

    const removeColumn = useCallback(
        (col) => {
            if (!selected.includes(col)) return;
            syncSelected(selected.filter((c) => c !== col));
        },
        [selected, syncSelected]
    );

    const reorderSelected = useCallback(
        (fromIndex, toIndex) => {
            if (fromIndex === toIndex) return;
            const list = [...selected];
            const [moved] = list.splice(fromIndex, 1);
            list.splice(toIndex, 0, moved);
            syncSelected(list);
        },
        [selected, syncSelected]
    );

    const handleSortingFieldChange = (field) => {
        setSortingField(field);
        setNewFields(prev => ({ ...prev, sorting: { field, order: sortingOrder } }));
    };

    const handleSortingOrderChange = (order) => {
        setSortingOrder(order);
        setNewFields(prev => ({ ...prev, sorting: { field: sortingField, order } }));
    };

    const NON_SORTABLE = ['richText', 'file', 'externalEntity', 'multipleExternalEntity', 'deepExternalEntity'];
    const sortableColumns = allColumns.filter(col => {
        const colConfig = props?.columns?.[col];
        return colConfig && !NON_SORTABLE.includes(colConfig.input_type);
    });

    // Drag & Drop native: start
    const onDragStartItem = (e, payload) => {
        const json = JSON.stringify(payload);
        e.dataTransfer.setData("text/plain", json); // per compatibilità
        e.dataTransfer.setData("application/json", json);
        e.dataTransfer.effectAllowed = "move";
        setDragging(payload); // { item, source, index }
    };

    const clearDnDState = () => {
        setDragging(null);
        setOverList(null);
        setPlaceholderIndex(null);
    };

    // Calcolo placeholderIndex quando si trascina sopra un item della lista "selected"
    const onDragOverSelectedItem = (e, itemIndex) => {
        e.preventDefault();
        e.stopPropagation();
        setOverList("selected");
        const rect = e.currentTarget.getBoundingClientRect();
        const isTopHalf = e.clientY < rect.top + rect.height / 2;
        setPlaceholderIndex(isTopHalf ? itemIndex : itemIndex + 1);
        e.dataTransfer.dropEffect = "move";
    };

    // Drag over su contenitore selected (quando non ci sono item)
    const onDragOverSelectedList = (e) => {
        e.preventDefault();
        setOverList("selected");
        setPlaceholderIndex(selected.length);
        e.dataTransfer.dropEffect = "move";
    };

    // Drag over su available per consentire drop (rimozione)
    const onDragOverAvailable = (e) => {
        e.preventDefault();
        setOverList("available");
        e.dataTransfer.dropEffect = "move";
    };

    // Drop sulla lista selected
    const onDropSelected = (e) => {
        e.preventDefault();
        e.stopPropagation();
        let data = null;
        try {
            const raw = e.dataTransfer.getData("application/json") || e.dataTransfer.getData("text/plain");
            data = JSON.parse(raw);
        } catch (_) { }

        const dropIndex =
            placeholderIndex == null ? selected.length : Math.max(0, Math.min(placeholderIndex, selected.length));

        if (!data) {
            clearDnDState();
            return;
        }

        if (data.source === "available") {
            if (!selected.includes(data.item)) addColumn(data.item, dropIndex);
            clearDnDState();
            return;
        }

        if (data.source === "selected") {
            const from = data.index;
            let to = dropIndex;
            if (to > from) to = to - 1; // aggiusta indice quando sposti verso il basso
            to = Math.max(0, Math.min(to, selected.length - 1));
            reorderSelected(from, to);
            clearDnDState();
            return;
        }

        clearDnDState();
    };

    // Drop su available: rimuove se proveniva da selected
    const onDropAvailable = (e) => {
        e.preventDefault();
        e.stopPropagation();
        let data = null;
        try {
            const raw = e.dataTransfer.getData("application/json") || e.dataTransfer.getData("text/plain");
            data = JSON.parse(raw);
        } catch (_) { }

        if (data && data.source === "selected") removeColumn(data.item);

        clearDnDState();
    };

    function closeModal() {
        // Chiudi senza cambiare la preferenza attiva esterna
        close(entityPreferences);
    }

    // --- Gestione preferenze multiple ---

    // Salva MODIFICHE sulla preferenza attualmente selezionata (update by id)
    async function savePreferences() {
        if (!activePrefId) {
            toast.info("Seleziona una preferenza da modificare o salva come nuova.", { theme: "colored" });
            return;
        }
        // Se stiamo rinominando, valida il nuovo nome
        if (renamingId === activePrefId) {
            const name = (renamingLabel || "").trim();
            if (!name) {
                toast.info("Inserisci un nome valido per la preferenza.", { theme: "colored" });
                return;
            }
            if (prefList.some((p) => p.id !== activePrefId && (p.label || "").toLowerCase() === name.toLowerCase())) {
                toast.error("Esiste già una preferenza con questo nome.", { theme: "colored" });
                return;
            }
        }
        setSaving(true);
        try {
            const current = prefList.find((p) => p.id === activePrefId);
            const body = {
                content: JSON.stringify(newFields),
                entity,
                label: renamingId === activePrefId ? renamingLabel : (current?.label ?? ""),
                isDefault: current?.isDefault ? 1 : 0,
            };
            const resp = await DataManager.updateItem("user_entity_preferences", body, activePrefId);
            if (resp.success === 1) {
                toast.success("Modifiche salvate sulla preferenza.", { theme: "colored" });
                // aggiorna cache locale
                setPrefList((prev) => prev.map((p) => (p.id === activePrefId ? { ...p, content: body.content, label: body.label } : p)));
                // Resetta stato rinomina
                setRenamingId(null);
                setRenamingLabel("");
                // Non applichiamo automaticamente: per applicarla, l'utente usa "Imposta".
            } else {
                toast.error("Salvataggio modifiche non riuscito.", { theme: "colored" });
            }
        } catch (err) {
            toast.error("Errore durante il salvataggio delle modifiche.", { theme: "colored" });
        }
        setSaving(false);
    }

    // Crea nuova preferenza con nome (sempre disponibile, anche mentre modifichi una esistente)
    const [preferenceName, setPreferenceName] = useState("");
    async function saveUserPreference() {
        const name = (preferenceName || "").trim();
        if (!name) {
            toast.info("Inserisci un nome per la nuova preferenza.", { theme: "colored" });
            return;
        }
        if (!newFields || !Array.isArray(newFields.columns) || newFields.columns.length === 0) {
            toast.info("Seleziona almeno una colonna.", { theme: "colored" });
            return;
        }
        if (prefList.some((p) => (p.label || "").toLowerCase() === name.toLowerCase())) {
            toast.error("Esiste già una preferenza con questo nome.", { theme: "colored" });
            return;
        }

        setSaving(true);
        try {
            const body = {
                content: JSON.stringify(newFields),
                entity,
                label: name,
                isDefault: 0,
            };
            const resp = await DataManager.insertItem("user_entity_preferences", body);
            if (resp.success === 1) {
                const newId = resp?.body?.id ?? resp?.body ?? null;
                const newPref = { id: newId, ...body };
                setPrefList((prev) => [...prev, newPref]);
                setActivePrefId(newId);
                toast.success("Preferenza creata. Per applicarla, usa 'Imposta'.", { theme: "colored" });
                setPreferenceName("");
            } else {
                toast.error("Creazione preferenza non riuscita.", { theme: "colored" });
            }
        } catch (err) {
            toast.error("Errore durante la creazione della preferenza.", { theme: "colored" });
        }
        setSaving(false);
    }

    // Carica una preferenza per MODIFICARLA (non la applica)
    function loadPreference(pref) {
        var label = pref.label ? pref.label : "(senza nome)"
        let content;
        try {
            content = typeof pref.content === "string" ? JSON.parse(pref.content) : pref.content;
        } catch (_) {
            content = pref.content || {};
        }
        const prefSelected = (content?.columns || []).filter((c) => allColumns.includes(c));
        setSelected(prefSelected);
        setNewFields({ columns: prefSelected, sorting: content?.sorting || null });
        setActivePrefId(pref.id);
        if (content?.sorting) {
            setSortingField(content.sorting.field || 'id');
            setSortingOrder(content.sorting.order || 'desc');
        } else {
            setSortingField('id');
            setSortingOrder('desc');
        }
        // Prepara il label per la modifica
        setRenamingId(pref.id);
        setRenamingLabel(pref.label || "");
        toast.info(<div><b>Stai modificando: {label}</b><br /><span className="small">Modifica il nome e/o le colonne, poi salva</span></div>, { theme: "colored" });
    }

    // Imposta come default la preferenza selezionata e rimuove il default dalle altre (applica)
    async function setDefaultPreference(prefId) {
        try {
            const currentDefault = prefList.find((p) => Number(p.isDefault) === 1);
            if (currentDefault && currentDefault.id === prefId) return; // già default

            // Disattiva il vecchio default
            if (currentDefault) {
                await DataManager.updateItem("user_entity_preferences", { isDefault: 0 }, currentDefault.id);
            }
            // Attiva il nuovo default
            await DataManager.updateItem("user_entity_preferences", { isDefault: 1 }, prefId);

            // Aggiorna stato locale
            setPrefList((prev) => prev.map((p) => ({ ...p, isDefault: Number(p.id === prefId) })));
            toast.success("Preferenza impostata come predefinita.", { theme: "colored" });
        } catch (err) {
            toast.error("Impossibile impostare come predefinita.", { theme: "colored" });
        }
    }

    // Elimina una preferenza
    async function deletePreference(prefId) {
        try {
            if (activePrefId === prefId) setActivePrefId(null);
            const resp = await (DataManager.deleteItem
                ? DataManager.deleteItem("user_entity_preferences", prefId)
                : DataManager.updateItem("user_entity_preferences", { _deleted: 1 }, prefId));
            if (resp.success === 1) {
                setPrefList((prev) => prev.filter((p) => p.id !== prefId));
                toast.success("Preferenza eliminata.", { theme: "colored" });
            } else {
                toast.error("Eliminazione non riuscita.", { theme: "colored" });
            }
        } catch (err) {
            toast.error("Errore durante l'eliminazione.", { theme: "colored" });
        }
    }


    // Render guard
    if (props === null || status === false || newFields === null) return null;

    const modalSize =
        settings.modalDefaultSize && settings.modalDefaultSize.entityConfig
            ? settings.modalDefaultSize.entityConfig
            : "xl";

    const isDraggingSelected = dragging?.source === "selected";

    return (
        <Modal show={status} onHide={closeModal} size={modalSize} scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    {LanguageProvider.get("defaults.options")} - {LanguageProvider.get("entity." + entity)}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Prima riga: Salva preferenze e Preferenze salvate */}
                <Row className="mb-3">
                    <Col lg={12}>
                        {/* Preferenze salvate */}
                        <Card>
                            <Card.Header>
                                <strong>Preferenze salvate</strong>
                                <div className="small text-muted">Per <em>applicare</em> una preferenza usa "Imposta". "Modifica" la apre nel pannello di sinistra senza applicarla.</div>
                            </Card.Header>
                            <ListGroup variant="flush">
                                {prefList.length === 0 && (
                                    <ListGroup.Item className="text-muted">Nessuna preferenza salvata.</ListGroup.Item>
                                )}
                                {prefList.map((p) => (
                                    <ListGroup.Item key={p.id} className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="text-warning" title={Number(p.isDefault) === 1 ? "Predefinita" : ""}>
                                                <FontAwesomeIcon icon="star" style={{ opacity: Number(p.isDefault) === 1 ? 1 : 0.25 }} />
                                            </span>
                                            <span className="fw-semibold">{p.label || "(senza nome)"}</span>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <Button size="sm" variant="outline-secondary" onClick={() => loadPreference(p)}>
                                                Modifica
                                            </Button>
                                            <Button size="sm" variant="primary" onClick={() => setDefaultPreference(p.id)}>
                                                Imposta
                                            </Button>
                                            <Button size="sm" variant="link" className="text-danger p-0" onClick={() => deletePreference(p.id)} title="Elimina">
                                                <FontAwesomeIcon icon="trash" />
                                            </Button>
                                        </div>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </Card>
                    </Col>
                </Row>

                {/* Seconda riga: Colonne selezionate e Colonne disponibili */}
                {activePrefId && (
                    <>
                        <div className="small text-muted mb-2">Stai modificando la preferenza: <b className="text-primary">{prefList.find((p) => p.id === activePrefId)?.label || "(senza nome)"}</b></div>
                    </>
                )}
                <Row>
                    <Col lg={6} className="mb-3">
                        <Card>
                            <Card.Header className="d-flex align-items-center justify-content-between">
                                <span>
                                    <strong>{LanguageProvider.get("defaults.chosen_columns")}</strong> <Badge bg="secondary">{selected.length}</Badge>
                                </span>
                            </Card.Header>

                            <div
                                onDragOver={onDragOverSelectedList}
                                onDrop={onDropSelected}
                                onDragLeave={() => {
                                    setOverList(null);
                                    setPlaceholderIndex(null);
                                }}
                                className={`list-group rounded-0 ${overList === "selected" ? "bg-light" : ""}`}
                                style={{ minHeight: 300, padding: 20 }}
                            >
                                {selected.length === 0 && (
                                    <div className="list-group-item text-muted">Trascina qui le colonne da usare</div>
                                )}

                                {selected.map((col, index) => {
                                    const label = LanguageProvider.getLabel(entity, col);
                                    return (
                                        <div key={col}>
                                            {/* Placeholder sopra l'item quando necessario */}
                                            {overList === "selected" && placeholderIndex === index && (
                                                <div className="list-group-item p-0">
                                                    <div style={{ height: 8, borderTop: "2px dashed #6c757d" }} />
                                                </div>
                                            )}

                                            <div
                                                className="list-group-item d-flex align-items-center justify-content-between small mb-1"
                                                draggable
                                                onDragStart={(e) => onDragStartItem(e, { item: col, source: "selected", index })}
                                                onDragOver={(e) => onDragOverSelectedItem(e, index)}
                                            >
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="text-muted" role="img" aria-label="Drag handle">
                                                        <FontAwesomeIcon icon="grip-vertical" />
                                                    </span>
                                                    <span>{label}</span>
                                                </div>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => removeColumn(col)}
                                                    aria-label="Rimuovi colonna"
                                                >
                                                    <FontAwesomeIcon icon="minus" />
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Placeholder in coda */}
                                {overList === "selected" && placeholderIndex === selected.length && (
                                    <div className="list-group-item p-0">
                                        <div style={{ height: 8, borderTop: "2px dashed #6c757d" }} />
                                    </div>
                                )}
                            </div>

                            <div className="p-3">
                                {activePrefId && (
                                    <>
                                        <InputGroup>
                                            <Form.Control
                                                value={renamingId === activePrefId ? renamingLabel : (prefList.find((p) => p.id === activePrefId)?.label || "")}
                                                onChange={(e) => {
                                                    if (renamingId !== activePrefId) {
                                                        setRenamingId(activePrefId);
                                                        setRenamingLabel(e.target.value);
                                                    } else {
                                                        setRenamingLabel(e.target.value);
                                                    }
                                                }}
                                                type="text"
                                                placeholder="Nome della preferenza"
                                            />
                                            <Button variant="primary" onClick={savePreferences} disabled={saving} className="w-50">
                                                {saving ? <Spinner size="sm" /> : "Salva modifiche"}
                                            </Button>
                                        </InputGroup>
                                        <div className="small text-muted text-center my-3">--- o ---</div>
                                    </>
                                )}

                                <div className="d-flex align-items-center gap-2">
                                    <InputGroup>
                                        <Form.Control
                                            placeholder="Nome della preferenza"
                                            value={preferenceName}
                                            onChange={(e) => setPreferenceName(e.target.value)}
                                            type="text"
                                        />
                                        <Button variant="success" onClick={saveUserPreference} disabled={saving} className="w-50">
                                            Salva come nuova
                                        </Button>
                                    </InputGroup>
                                </div>
                            </div>
                        </Card>
                    </Col>

                    <Col lg={6} className="mb-3">
                        <Card className="mb-3">
                            <Card.Header>
                                <strong><FontAwesomeIcon icon="sort" className="me-1" /> Ordinamento predefinito</strong>
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-2">
                                    <Col sm={8}>
                                        <Form.Select
                                            size="sm"
                                            value={sortingField}
                                            onChange={(e) => handleSortingFieldChange(e.target.value)}
                                        >
                                            {sortableColumns.map(col => (
                                                <option key={col} value={col}>
                                                    {LanguageProvider.getLabel(entity, col)}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                    <Col sm={4}>
                                        <Form.Select
                                            size="sm"
                                            value={sortingOrder}
                                            onChange={(e) => handleSortingOrderChange(e.target.value)}
                                        >
                                            <option value="asc">Crescente (A→Z)</option>
                                            <option value="desc">Decrescente (Z→A)</option>
                                        </Form.Select>
                                    </Col>
                                </Row>
                                <div className="small text-muted mt-1">L'ordinamento viene applicato al caricamento della lista.</div>
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Header className="d-flex align-items-center justify-content-between">
                                <span>
                                    <strong>{LanguageProvider.get("defaults.available_columns")}</strong> <Badge bg="secondary">{allColumns.length}</Badge>
                                </span>
                            </Card.Header>

                            <div
                                onDragOver={onDragOverAvailable}
                                onDrop={onDropAvailable}
                                onDragLeave={() => setOverList(null)}
                                className={`list-group rounded-0 ${overList === "available" ? "bg-light" : ""}`}
                                style={{ minHeight: 300, padding: 20 }}
                            >
                                {allColumns.map((col) => {
                                    const label = LanguageProvider.getLabel(entity, col);
                                    const inUse = selected.includes(col);

                                    if (inUse) {
                                        return (
                                            <div
                                                key={col}
                                                className="list-group-item d-flex align-items-center justify-content-between text-dark small mb-1 border-0"
                                                style={{ background: "rgb(255 208 208)" }}
                                            >
                                                <span>{label}</span>
                                                <Badge bg="secondary">{LanguageProvider.get("defaults.used")}</Badge>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div
                                            key={col}
                                            className="list-group-item d-flex align-items-center justify-content-between text-dark small mb-1 border-0"
                                            style={{ background: "rgb(201 241 200)" }}
                                            draggable
                                            onDragStart={(e) => onDragStartItem(e, { item: col, source: "available", index: -1 })}
                                        >
                                            <span>{label}</span>
                                            <div className="d-flex align-items-center gap-2">
                                                <Badge bg="success">{LanguageProvider.get("defaults.available")}</Badge>
                                                <Button variant="outline-primary" size="sm" onClick={() => addColumn(col)} aria-label="Aggiungi colonna">
                                                    <FontAwesomeIcon icon="plus" />
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={closeModal}>
                    {LanguageProvider.get("defaults.close")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
